/**
 * Filename sanitization utilities for cross-platform compatibility
 * 
 * Ensures filenames work across web, native apps, and different filesystems
 * by removing or replacing problematic characters.
 */

/**
 * Sanitize a filename for safe storage across platforms
 * 
 * - Preserves file extension
 * - Removes/replaces special characters: ()[]{}<>:"'`/\|?*
 * - Replaces spaces with hyphens
 * - Handles unicode safely
 * - Prevents empty names
 * 
 * @param filename - Original filename to sanitize
 * @returns Sanitized filename safe for all platforms
 * 
 * @example
 * sanitizeFilename("Image (3).png") // "image-3.png"
 * sanitizeFilename("Group 2.png") // "group-2.png"
 * sanitizeFilename("<bad>.png") // "bad.png"
 */
export function sanitizeFilename(filename: string): string {
  // Split into base and extension
  const lastDotIndex = filename.lastIndexOf('.')
  const hasExtension = lastDotIndex > 0 && lastDotIndex < filename.length - 1
  
  const base = hasExtension ? filename.slice(0, lastDotIndex) : filename
  const extension = hasExtension ? filename.slice(lastDotIndex).toLowerCase() : ''
  
  // Sanitize base name
  let sanitized = base
    .toLowerCase() // Consistent casing
    .replaceAll(/\s+/g, '-') // Spaces to hyphens
    .replaceAll(/["'()*/:<>?[\\\]`{|}]/g, '') // Remove problematic chars
    .replaceAll(/-+/g, '-') // Collapse multiple hyphens
    .replaceAll(/^[._-]+|[._-]+$/g, '') // Trim leading/trailing special chars
  
  // Prevent empty base
  if (sanitized.length === 0) {
    sanitized = 'file'
  }
  
  return sanitized + extension
}

/**
 * Create a unique filename by appending a number if name exists in the set
 * 
 * @param desiredName - The preferred filename
 * @param existingNames - Set of already-used names
 * @returns Unique filename, possibly with numeric suffix
 * 
 * @example
 * makeUniqueFilename("file.png", new Set(["file.png"])) // "file-1.png"
 * makeUniqueFilename("file.png", new Set(["file.png", "file-1.png"])) // "file-2.png"
 */
export function makeUniqueFilename(desiredName: string, existingNames: Set<string>): string {
  if (!existingNames.has(desiredName)) {
    return desiredName
  }
  
  // Split name and extension
  const lastDotIndex = desiredName.lastIndexOf('.')
  const hasExtension = lastDotIndex > 0 && lastDotIndex < desiredName.length - 1
  const base = hasExtension ? desiredName.slice(0, lastDotIndex) : desiredName
  const extension = hasExtension ? desiredName.slice(lastDotIndex) : ''
  
  // Try appending numbers until we find a unique name
  let counter = 1
  let uniqueName = `${base}-${String(counter)}${extension}`

  while (existingNames.has(uniqueName)) {
    counter++
    uniqueName = `${base}-${String(counter)}${extension}`
  }
  
  return uniqueName
}
